<template>
  <div
    ref="scroller"
    :class="['ai-chat-message-list', showPlaceholder && !boxed ? '-suggesting' : '']"
    @scroll="onScroll"
  >
    <div
      v-if="loading && messages.length === 0"
      class="_spinner"
    >
      <m-spinner size="xs" />
    </div>
    <div
      v-else-if="showPlaceholder"
      :class="['_placeholder', boxed ? '-boxed' : '']"
    >
      <div class="_avatar icon-hover-host">
        <m-icon
          type="astronaut"
          size="26"
        />
      </div>
      <div class="_greeting">
        {{ $t('aiChat.emptyState') }}
      </div>
      <ai-chat-skill-suggestions
        v-if="!boxed"
        @use="$emit('use-skill', $event)"
      />
    </div>
    <div
      v-else
      ref="content"
      :class="['_content', boxed ? '-boxed' : '']"
    >
      <ai-chat-message
        v-for="(message, index) in messages"
        :key="message.uid"
        :data-uid="message.uid"
        :message="message"
        :retryable="retryable && failure === null && index === messages.length - 1"
        @retry="$emit('retry')"
      />
      <ai-chat-message
        v-if="outgoing !== null"
        :message="outgoing"
        sending
      />
      <ai-chat-message-failure
        v-if="failure !== null"
        :reason="failure"
        :retryable="retryable"
        @retry="$emit('retry')"
      />
      <ai-chat-message
        v-if="pending && outgoing === null && streamedAnswer !== null"
        :message="{ role: aiChatMessageRole.assistant, content: streamedAnswer }"
        streaming
      />
      <div
        v-else-if="pending && outgoing === null && connection !== AI_CHAT_CONNECTION.syncing"
        class="_pending"
      >
        {{ connection === AI_CHAT_CONNECTION.connecting ? $t('aiChat.connecting') : $t('aiChat.thinking') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import AiChatMessage from '@/components/ai-chat/AiChatMessage.vue';
import AiChatMessageFailure from '@/components/ai-chat/AiChatMessageFailure.vue';
import AiChatSkillSuggestions from '@/components/ai-chat/AiChatSkillSuggestions.vue';
import useAiChatSession from '@/composables/ai-chat/ai-chat-session';
import { AI_CHAT_CONNECTION } from '@/lib/constants';
import { AT_BOTTOM, anchorFor, offsetFor } from '@/lib/ai-chat-scroll';
import { aiChatMessageRole } from 'shared/constants.json';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { isEmptyChat } from '@/lib/ai-chat-list';

const props = defineProps({
  chatId: {
    type: Number,
    default: null,
  },
  boxed: {
    type: Boolean,
    default: false,
  },
  messages: {
    type: Array,
    required: true,
  },
  outgoing: {
    type: Object,
    default: null,
  },
  pending: {
    type: Boolean,
    default: false,
  },
  streamedAnswer: {
    type: String,
    default: null,
  },
  connection: {
    type: String,
    default: null,
  },
  failure: {
    type: String,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  retryable: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['retry', 'use-skill']);

const showPlaceholder = computed(() => isEmptyChat(props));

const scroller = ref(null);
const content = ref(null);

const scrollAnchor = () => useAiChatSession(props.chatId).scrollAnchor;

const measuredMessages = () => [...scroller.value.querySelectorAll('[data-uid]')]
  .map((el) => ({ uid: Number(el.dataset.uid), offsetTop: el.offsetTop, height: el.offsetHeight }));

let programmaticTop = null;
let knownScrollHeight = 0;
let anchorFrame = null;

const applyAnchor = () => {
  const el = scroller.value;
  if (el === null) {
    return;
  }
  knownScrollHeight = el.scrollHeight;
  const anchor = scrollAnchor().value;
  const top = anchor === AT_BOTTOM ? el.scrollHeight : offsetFor(measuredMessages(), anchor);
  programmaticTop = Math.max(0, Math.min(top, el.scrollHeight - el.clientHeight));
  el.scrollTop = programmaticTop;
};

const storeAnchor = () => {
  anchorFrame = null;
  const el = scroller.value;
  if (el === null) {
    return;
  }
  const items = measuredMessages();
  if (items.length === 0) {
    return;
  }
  scrollAnchor().value = anchorFor(items, el.scrollTop, {
    viewportHeight: el.clientHeight,
    scrollHeight: el.scrollHeight,
  });
};

const onScroll = () => {
  if (scroller.value === null) {
    return;
  }
  const { scrollTop, scrollHeight } = scroller.value;
  if (scrollHeight !== knownScrollHeight) {
    knownScrollHeight = scrollHeight;
    return;
  }
  if (programmaticTop !== null && Math.abs(scrollTop - programmaticTop) <= 1) {
    programmaticTop = null;
    return;
  }
  if (anchorFrame === null) {
    anchorFrame = requestAnimationFrame(storeAnchor);
  }
};

const restoreAnchor = () => nextTick(applyAnchor);

watch(() => props.outgoing, (outgoing) => {
  if (outgoing !== null) {
    scrollAnchor().value = AT_BOTTOM;
    restoreAnchor();
  }
});

watch(() => props.chatId, restoreAnchor);

let observer = null;

watch(content, (el, previous) => {
  if (observer === null) {
    return;
  }
  if (previous) {
    observer.unobserve(previous);
  }
  if (el) {
    observer.observe(el);
  }
}, { flush: 'post' });

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(applyAnchor);
    if (content.value !== null) {
      observer.observe(content.value);
    }
  }
  restoreAnchor();
});

onBeforeUnmount(() => {
  observer?.disconnect();
  if (anchorFrame !== null) {
    cancelAnimationFrame(anchorFrame);
  }
});
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  @import "shared/assets/scss/_box-shadow.scss";

  .ai-chat-message-list {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;

    // both ends have to clear a fade, or the first and the last message stay washed out once
    // there is nothing left to scroll
    padding: $ai-chat-fade-height 2rem;
    overflow-y: auto;

    &.-suggesting {
      padding-bottom: 1.2rem;
    }

    ._content {
      display: flex;
      flex-direction: column;
      gap: .8rem;

      &.-boxed {
        width: 100%;
        max-width: $ai-chat-full-screen-width;
        margin: 0 auto;
      }
    }

    ._spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }

    ._placeholder {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      gap: 1.2rem;
      height: 100%;

      ._avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 5.2rem;
        height: 5.2rem;
        margin: 0 0 .4rem .8rem;
        color: $font-color-primary;
        background-color: white;
        border-radius: 50%;

        @include box_shadow(1);
      }

      ._greeting {
        padding: 0 .8rem;
        font-size: $font-size-5;
        font-weight: $font-weight-semibold;
      }

      &.-boxed {
        align-items: center;
        justify-content: center;
        text-align: center;

        ._avatar {
          margin: 0 0 .4rem;
        }

        ._greeting {
          font-size: $font-size-8;
          font-weight: $font-weight-medium;
        }
      }
    }

    ._pending {
      padding: .4rem 0;
      color: $font-color-secondary;
    }
  }
</style>
